import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
export let lastModified = undefined;
export let frontmatter = {
  "title": "Turborepo 1.8",
  "description": "Turborepo 1.8 brings better flexibility and more control to your codebase by improving turbo's understanding of your workspaces.",
  "date": "2023/02/22",
  "tag": "web development"
};
export let structuredData = {
  "contents": [{
    "heading": "",
    "content": "Turborepo 1.8 brings better flexibility and more control to your codebase by improving turbo's understanding of your workspaces."
  }, {
    "heading": "",
    "content": "Workspace Configurations: You can now add a turbo.json configuration file in a workspace to override the root configuration in your repository."
  }, {
    "heading": "",
    "content": "Automatic Workspace Scoping: Global turbo now automatically infers your current workspace so that it only runs that workspace’s tasks."
  }, {
    "heading": "",
    "content": "Easier Migrations: Automatically migrate to new versions of turbo with npx @turbo/codemod migrate."
  }, {
    "heading": "",
    "content": "Update today by running npx @turbo/codemod migrate."
  }, {
    "heading": "workspace-configurations",
    "content": "In workspace directories, you can now add a turbo.json to:"
  }, {
    "heading": "workspace-configurations",
    "content": "add tasks specific to that workspace"
  }, {
    "heading": "workspace-configurations",
    "content": "override configuration for tasks"
  }, {
    "heading": "workspace-configurations",
    "content": "This will enable teams to scale ownership of the projects in their monorepos by moving away from global configuration to fine-grain control over tasks in workspaces."
  }, {
    "heading": "workspace-configurations",
    "content": "For example, imagine your monorepo has a Next.js app and a SvelteKit app, and you want to use Turborepo to cache outputs of the build task. The Next.js build script creates a .next directory, whereas SvelteKit creates a .svelte-kit directory. Instead of adding both build directories in your root outputs, you can define the outputs key in the workspace instead:"
  }, {
    "heading": "workspace-configurations",
    "content": "The extends key in the Workspace Configurations enables workspace owners to use the best of the root turbo.json and customize the parts that makes their app different (The \"//\" sigil will look familiar if you’re used to defining tasks to run from your root)."
  }, {
    "heading": "workspace-configurations",
    "content": "Keys that are declared will replace the key from the root if those keys exist, overriding what is defined in your root configuration. Keys that are not declared are inherited from the root config."
  }, {
    "heading": "workspace-configurations",
    "content": "In the example above, outputs is customized for both apps, while dependsOn is configured by the root turbo.json and remains \"^codegen\"."
  }, {
    "heading": "workspace-configurations",
    "content": "Learn more in the docs."
  }, {
    "heading": "automatic-workspace-scoping",
    "content": "In Turborepo v1.7, turbo became globally installable, giving you the power to run your tasks from anywhere in your codebase. However, turbo would still run tasks from the root, running tasks in other workspaces that you may not have intended to run."
  }, {
    "heading": "automatic-workspace-scoping",
    "content": "With 1.8, turbo will automatically detect the workspace you are in and generate the --filter syntax to scope your task to that workspace."
  }, {
    "heading": "automatic-workspace-scoping",
    "content": "As an example, if your current directory is apps/admin and you use the turbo build command, turbo will run turbo build --filter=admin under the hood, focusing on the workspace that you are working on."
  }, {
    "heading": "easier-migrations",
    "content": "Manually running individual codemods in the correct order is no longer required when upgrading Turborepo versions. @turbo/codemod now provides a simple migrate command which both upgrades your repo to the specified version (latest by default) of turbo, and runs any codemods required."
  }, {
    "heading": "easier-migrations",
    "content": "Try it out now with npx @turbo/codemod migrate."
  }, {
    "heading": "community",
    "content": "Since releasing Turborepo v1.7 we've seen incredible adoption and community growth:"
  }, {
    "heading": "community",
    "content": "19.6k+ GitHub Stars"
  }, {
    "heading": "community",
    "content": "987k weekly NPM downloads"
  }, {
    "heading": "community",
    "content": "42 years of compute time saved through Remote Caching on Vercel"
  }, {
    "heading": "community",
    "content": "Turborepo is the result of the combined work of all of its contributors, including our core team."
  }, {
    "heading": "community",
    "content": "Thank you for your continued support, feedback, and collaboration to make Turborepo your build tool of choice."
  }],
  "headings": [{
    "id": "workspace-configurations",
    "content": "Workspace Configurations"
  }, {
    "id": "automatic-workspace-scoping",
    "content": "Automatic Workspace Scoping"
  }, {
    "id": "easier-migrations",
    "content": "Easier Migrations"
  }, {
    "id": "community",
    "content": "Community"
  }]
};
import {Tabs, Tab} from '#components/tabs';
import {Authors} from '#components/authors';
import {Badge} from '#components/badge';
import {Date} from '#components/blog/date';
export const toc = [{
  depth: 2,
  url: "#workspace-configurations",
  title: _jsx(_Fragment, {
    children: "Workspace Configurations"
  })
}, {
  depth: 2,
  url: "#automatic-workspace-scoping",
  title: _jsx(_Fragment, {
    children: "Automatic Workspace Scoping"
  })
}, {
  depth: 2,
  url: "#easier-migrations",
  title: _jsx(_Fragment, {
    children: "Easier Migrations"
  })
}, {
  depth: 2,
  url: "#community",
  title: _jsx(_Fragment, {
    children: "Community"
  })
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    em: "em",
    h2: "h2",
    li: "li",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ul: "ul",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx("h1", {
      className: "text-center",
      children: "Turborepo 1.8"
    }), "\n", "\n", _jsx(Date, {
      children: "Wednesday, February 22th, 2023"
    }), "\n", _jsx(Authors, {
      authors: ['gregsoltis', 'nathanhammond', 'tomknickman', 'anthonyshew', 'jaredpalmer', 'mehulkar', 'chrisolszewski', 'nicholasyang', 'alexanderlyon']
    }), "\n", _jsxs(_components.p, {
      children: ["Turborepo 1.8 brings better flexibility and more control to your codebase by improving ", _jsx(_components.code, {
        children: "turbo"
      }), "'s understanding of your workspaces."]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "#workspace-configurations",
          children: _jsx(_components.strong, {
            children: "Workspace Configurations"
          })
        }), ": You can now add a ", _jsx(_components.code, {
          children: "turbo.json"
        }), " configuration file in a workspace to override the root configuration in your repository."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "#automatic-workspace-scoping",
          children: _jsx(_components.strong, {
            children: "Automatic Workspace Scoping"
          })
        }), ": Global ", _jsx(_components.code, {
          children: "turbo"
        }), " now automatically infers your current workspace so that it only runs that workspace’s tasks."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.a, {
          href: "#easier-migrations",
          children: _jsx(_components.strong, {
            children: "Easier Migrations"
          })
        }), ": Automatically migrate to new versions of ", _jsx(_components.code, {
          children: "turbo"
        }), " with ", _jsx(_components.code, {
          children: "npx @turbo/codemod migrate"
        }), "."]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["Update today by running ", _jsx(_components.code, {
        children: "npx @turbo/codemod migrate"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "workspace-configurations",
      children: "Workspace Configurations"
    }), "\n", _jsxs(_components.p, {
      children: ["In workspace directories, you can now add a ", _jsx(_components.code, {
        children: "turbo.json"
      }), " to:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "add tasks specific to that workspace"
      }), "\n", _jsx(_components.li, {
        children: "override configuration for tasks"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "This will enable teams to scale ownership of the projects in their monorepos by moving away from global configuration to fine-grain control over tasks in workspaces."
    }), "\n", _jsxs(_components.p, {
      children: ["For example, imagine your monorepo has a Next.js app and a SvelteKit app, and you want to use Turborepo to cache outputs of the ", _jsx(_components.code, {
        children: "build"
      }), " task. The Next.js ", _jsx(_components.code, {
        children: "build"
      }), " script creates a ", _jsx(_components.code, {
        children: ".next"
      }), " directory, whereas SvelteKit creates a ", _jsx(_components.code, {
        children: ".svelte-kit"
      }), " directory. Instead of adding both build directories in your root ", _jsx(_components.code, {
        children: "outputs"
      }), ", you can define the ", _jsx(_components.code, {
        children: "outputs"
      }), " key in the workspace instead:"]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "turbo.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"pipeline\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "    \"build\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "      \"dependsOn\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\"^codegen\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "]"
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-comment)",
                "--shiki-dark": "var(--shiki-token-comment)"
              },
              children: "      // no need to define outputs here!"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "    }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "apps/my-nextjs-app/turbo.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"extends\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\"//\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "]"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"pipeline\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "    \"build\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-comment)",
                "--shiki-dark": "var(--shiki-token-comment)"
              },
              children: "      // dependsOn is inherited from root"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "      \"outputs\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\".next/**\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ","
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: " \"!.next/cache/**\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "]"
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "    }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "apps/my-sveltekit-app/turbo.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"extends\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\"//\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "]"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"pipeline\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "    \"build\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-comment)",
                "--shiki-dark": "var(--shiki-token-comment)"
              },
              children: "      // dependsOn is inherited from root"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "      \"outputs\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " ["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: "\".svelte-kit/**\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "]"
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "    }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["The ", _jsx(_components.code, {
        children: "extends"
      }), " key in the Workspace Configurations enables workspace owners to use the best of the root ", _jsx(_components.code, {
        children: "turbo.json"
      }), " and customize the parts that makes their app different (The ", _jsx(_components.code, {
        children: "\"//\""
      }), " sigil will look familiar if you’re used to ", _jsx(_components.a, {
        href: "/docs/crafting-your-repository/configuring-tasks#registering-root-tasks",
        children: "defining tasks to run from your root"
      }), ")."]
    }), "\n", _jsxs(_components.p, {
      children: ["Keys that ", _jsx(_components.strong, {
        children: "are"
      }), " declared will replace the key from the root if those keys exist, overriding what is defined in your root configuration. Keys that ", _jsx(_components.strong, {
        children: "are not"
      }), " declared are inherited from the root config."]
    }), "\n", _jsxs(_components.p, {
      children: ["In the example above, ", _jsx(_components.code, {
        children: "outputs"
      }), " is customized for both apps, while ", _jsx(_components.code, {
        children: "dependsOn"
      }), " is configured by the root ", _jsx(_components.code, {
        children: "turbo.json"
      }), " and remains ", _jsx(_components.code, {
        children: "\"^codegen\""
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "/docs/crafting-your-repository/structuring-a-repository#specifying-packages-in-a-monorepo",
        children: "Learn more in the docs"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "automatic-workspace-scoping",
      children: "Automatic Workspace Scoping"
    }), "\n", _jsxs(_components.p, {
      children: ["In ", _jsx(_components.a, {
        href: "/blog/turbo-1-7-0",
        children: "Turborepo v1.7"
      }), ", ", _jsx(_components.code, {
        children: "turbo"
      }), " became globally installable, giving you the power to run your tasks from anywhere in your codebase. However, ", _jsx(_components.code, {
        children: "turbo"
      }), " would still run tasks from the root, running tasks in other workspaces that you may not have intended to run."]
    }), "\n", _jsxs(_components.p, {
      children: ["With 1.8, ", _jsx(_components.code, {
        children: "turbo"
      }), " will automatically detect the workspace you are in and generate ", _jsxs(_components.a, {
        href: "/docs/reference/run#--filter-string",
        children: ["the ", _jsx(_components.code, {
          children: "--filter"
        }), " syntax"]
      }), " to scope your task to that workspace."]
    }), "\n", _jsxs(_components.p, {
      children: ["As an example, if your current directory is ", _jsx(_components.code, {
        children: "apps/admin"
      }), " and you use the ", _jsx(_components.code, {
        children: "turbo build"
      }), " command, ", _jsx(_components.code, {
        children: "turbo"
      }), " will run ", _jsx(_components.code, {
        children: "turbo build --filter=admin"
      }), " under the hood, focusing on the workspace that you are working on."]
    }), "\n", _jsx(_components.h2, {
      id: "easier-migrations",
      children: "Easier Migrations"
    }), "\n", _jsxs(_components.p, {
      children: ["Manually running individual codemods in the correct order is no longer required when upgrading Turborepo versions. ", _jsx(_components.code, {
        children: "@turbo/codemod"
      }), " now provides a simple ", _jsx(_components.code, {
        children: "migrate"
      }), " command which both upgrades your repo to the specified version (", _jsx(_components.code, {
        children: "latest"
      }), " by default) of ", _jsx(_components.code, {
        children: "turbo"
      }), ", ", _jsx(_components.em, {
        children: "and"
      }), " runs any codemods required."]
    }), "\n", _jsxs(_components.p, {
      children: ["Try it out now with ", _jsx(_components.code, {
        children: "npx @turbo/codemod migrate"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      id: "community",
      children: "Community"
    }), "\n", _jsxs(_components.p, {
      children: ["Since releasing ", _jsx(_components.a, {
        href: "/blog/turbo-1-7-0",
        children: "Turborepo v1.7"
      }), " we've seen incredible adoption and community growth:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "https://github.com/vercel/turbo",
          children: "19.6k+ GitHub Stars"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.a, {
          href: "https://www.npmjs.com/package/turbo",
          children: "987k weekly NPM downloads"
        })
      }), "\n", _jsxs(_components.li, {
        children: ["42 years of compute time saved through ", _jsx(_components.a, {
          href: "https://vercel.com/docs/concepts/monorepos/remote-caching",
          children: "Remote Caching on Vercel"
        })]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Turborepo is the result of the combined work of all of its contributors, including our core team."
    }), "\n", _jsx(_components.p, {
      children: "Thank you for your continued support, feedback, and collaboration to make Turborepo your build tool of choice."
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
